import { defineMessages } from 'react-intl'
import productLabels from 'i18n/productLabels'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCameraPlusIcon from 'LuxKit/Icons/line/LineCameraPlusIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineDrinksTrayIcon from 'LuxKit/Icons/line/LineDrinksTrayIcon'
import LineFootballBallIcon from 'LuxKit/Icons/line/LineFootballBallIcon'
import LineIslandIcon from 'LuxKit/Icons/line/LineIslandIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineTreesIcon from 'LuxKit/Icons/line/LineTreesIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'

const messages = defineMessages({
  topAttractions: {
    id: 'E6p5kSMlqsK0qta2J3S-',
    defaultMessage: 'Top attractions',
    description: 'Explore category label for top attractions on the trip planner map',
  },
  restaurants: {
    id: 'dGN1D38X0F2rBTrqbPSj',
    defaultMessage: 'Restaurants',
    description: 'Explore category label for restaurants on the trip planner map',
  },
  cafes: {
    id: 'rvBdCRkwDuS4dyHef7GS',
    defaultMessage: 'Cafes',
    description: 'Explore category label for cafes on the trip planner map',
  },
  parks: {
    id: 'c_m0Qq-yd9laoUDZtEzo',
    defaultMessage: 'Parks',
    description: 'Explore category label for parks on the trip planner map',
  },
  shopping: {
    id: 'lWAJI_T8cadoJg486ZmZ',
    defaultMessage: 'Shopping',
    description: 'Explore category label for shopping on the trip planner map',
  },
  museums: {
    id: 'v83i7kN65Qqvj4P-d1E9',
    defaultMessage: 'Museums',
    description: 'Explore category label for museums on the trip planner map',
  },
  bars: {
    id: 'KPu8gCTx6Jtl_9qUEdSs',
    defaultMessage: 'Bars',
    description: 'Explore category label for bars on the trip planner map',
  },
  sports: {
    id: '1lkPhgjBZlVSBGojWO9R',
    defaultMessage: 'Sports',
    description: 'Explore category label for sports on the trip planner map',
  },
  beaches: {
    id: '04_39AJU5yfi02CAZhQP',
    defaultMessage: 'Beaches',
    description: 'Explore category label for beaches on the trip planner map',
  },
  experiences: {
    id: 'nQtODE_HEKnifEaON7OD',
    defaultMessage: 'Experiences',
    description: 'Explore category label for experiences on the trip planner map',
  },
})

export type ChatExploreCategoryKey =
  | 'hotels'
  | 'experiences'
  | 'top-attractions'
  | 'restaurants'
  | 'cafes'
  | 'parks'
  | 'shopping'
  | 'museums'
  | 'bars'
  | 'sports'
  | 'beaches'

export const chatExploreCategories = [
  {
    key: 'hotels' as const,
    labelMessage: productLabels.hotels,
    icon: LineBedIcon,
    source: 'le-hotels' as const,
  },
  {
    key: 'experiences' as const,
    labelMessage: messages.experiences,
    icon: LineTicketIcon,
    source: 'le-experiences' as const,
  },
  {
    key: 'top-attractions' as const,
    labelMessage: messages.topAttractions,
    icon: LineCameraPlusIcon,
    source: 'google-places' as const,
  },
  {
    key: 'restaurants' as const,
    labelMessage: messages.restaurants,
    icon: LineRestaurantIcon,
    source: 'google-places' as const,
  },
  {
    key: 'cafes' as const,
    labelMessage: messages.cafes,
    icon: LineCoffeeIcon,
    source: 'google-places' as const,
  },
  {
    key: 'parks' as const,
    labelMessage: messages.parks,
    icon: LineTreesIcon,
    source: 'google-places' as const,
  },
  {
    key: 'shopping' as const,
    labelMessage: messages.shopping,
    icon: LineShoppingBagIcon,
    source: 'google-places' as const,
  },
  {
    key: 'museums' as const,
    labelMessage: messages.museums,
    icon: LineUniversityIcon,
    source: 'google-places' as const,
  },
  {
    key: 'bars' as const,
    labelMessage: messages.bars,
    icon: LineDrinksTrayIcon,
    source: 'google-places' as const,
  },
  {
    key: 'sports' as const,
    labelMessage: messages.sports,
    icon: LineFootballBallIcon,
    source: 'google-places' as const,
  },
  {
    key: 'beaches' as const,
    labelMessage: messages.beaches,
    icon: LineIslandIcon,
    source: 'google-places' as const,
  },
] as const

export type ChatExploreCategory = (typeof chatExploreCategories)[number]

export type ChatExploreCategorySource = ChatExploreCategory['source']
export type LEExploreCategorySource = Extract<ChatExploreCategorySource, 'le-hotels' | 'le-experiences'>

// Hand-built English, not `category.labelMessage.defaultMessage` -- the locale build
// rewrites `defaultMessage` at build time for any non-default BUILD_LOCALE, so reading
// it is not a safe "always English" escape hatch. Activity
// displayText is LLM-context only, never customer-facing, and stays English regardless of locale,
// same rule as every other kind in this family (e.g. buildItemStatusChangedActivityEntry's
// englishStatusLabel).
const ENGLISH_EXPLORE_CATEGORY_LABELS: Record<ChatExploreCategoryKey, string> = {
  hotels: 'Hotels',
  experiences: 'Experiences',
  'top-attractions': 'Top attractions',
  restaurants: 'Restaurants',
  cafes: 'Cafes',
  parks: 'Parks',
  shopping: 'Shopping',
  museums: 'Museums',
  bars: 'Bars',
  sports: 'Sports',
  beaches: 'Beaches',
}

export function englishExploreCategoryLabel(key: ChatExploreCategoryKey): string {
  return ENGLISH_EXPLORE_CATEGORY_LABELS[key]
}

/** Shared so this lookup has nowhere to be inlined a third time. */
export function exploreCategoryKeyForLESource(source: LEExploreCategorySource): ChatExploreCategoryKey {
  return source === 'le-experiences' ? 'experiences' : 'hotels'
}

/**
 * What Google is asked, not what the chip says: a bare "Sports" returns sportswear shops. Shared,
 * because a drawer query differing by a word from the map's is a different cache entry.
 */
export function exploreCategoryQueryLabel(key: ChatExploreCategoryKey): string {
  return key === 'sports' ? 'Sports venues' : englishExploreCategoryLabel(key)
}
